/**
 * The city screen.
 *
 * Its own module rather than more of main.ts, which was already past a thousand lines
 * before this existed. The map view and the city view share an engine and nothing else.
 *
 * The design rule here is the one from the brief: the player must always be able to
 * see WHY a number is what it is. So the food line shows what was grown, what was
 * eaten and what is left over rather than a single surplus, the growth line says what
 * the city is actually waiting on, and every tile the citizens work is visible and
 * clickable rather than being decided somewhere the player cannot see.
 */

import { isBesieged } from '../core/cities/siege.js';
import type { Content } from '../../content/schema/index.js';
import { resourceAccess, resourceBlocker } from '../core/resources/access.js';
import { resourceLine } from './resource-text.js';
import type { Engine } from '../core/engine.js';
import { ERROR_TEXT } from '../core/engine.js';
import {
  canWorkTile,
  cityIndex,
  cityYield,
  foodIsWasted,
  foodSurplus,
  growthThreshold,
  cityCentreYield,
  workedYield,
  workableTiles,
} from '../core/cities/index.js';
import {
  buildBlocker,
  buildableItems,
  comingSoon,
  itemTech,
  heldForPopulation,
  capText,
  isAtSizeCap,
  itemCost,
  itemName,
  sizeCap,
  switchCost,
} from '../core/cities/production.js';
import { budget, dragFraction, effectiveYield, mood, spareCitizens, specialistsOf } from '../core/cities/economy.js';
import { wonderBlocker, wonderUnderwayIn } from '../core/cities/wonders.js';
import { cultureLine } from './culture-text.js';
import { hexCorners, hexToPixel, pixelToHex, type Hex } from '../core/hex/index.js';
import { hexIndex, indexToHex } from '../core/state/grid.js';
import type { City, ProductionItem } from '../core/state/types.js';

const PICKER_TILE = 34;

export interface CityScreen {
  open(cityId: number): void;
  close(): void;
  readonly isOpen: boolean;
  refresh(): void;
}

export interface CityScreenOptions {
  getEngine: () => Engine;
  /** Called after any command, so the map view can redraw and the log can be written. */
  onChanged: (message?: string, kind?: 'ok' | 'err') => void;
}

export function createCityScreen(options: CityScreenOptions): CityScreen {
  const root = document.createElement('div');
  root.className = 'city-screen';
  root.hidden = true;
  root.innerHTML = `
    <div class="city-panel">
      <header>
        <h2 id="cs-name">City</h2>
        <div class="cs-sub" id="cs-sub"></div>
        <button id="cs-close" title="Close (Esc)">Close</button>
      </header>

      <div class="cs-status" id="cs-status"></div>

      <div class="cs-body">
        <section class="cs-tiles">
          <h3>Land</h3>
          <canvas id="cs-canvas" width="360" height="330"></canvas>
          <div class="cs-tilehint" id="cs-tilehint">Click a tile to work it or set it aside.</div>
          <button id="cs-auto">Return to the governor</button>
          <div class="cs-specialists">
            <label for="cs-spec">Citizens with no tile work as</label>
            <select id="cs-spec"></select>
            <div class="dim" id="cs-spec-note"></div>
          </div>
        </section>

        <section class="cs-figures">
          <h3>Figures</h3>
          <dl class="readout" id="cs-yields"></dl>
          <h3>Built</h3>
          <div class="cs-built" id="cs-built"></div>
        </section>

        <section class="cs-production">
          <h3>Building</h3>
          <div class="cs-queue" id="cs-queue"></div>
          <h3>Available</h3>
          <div class="cs-available" id="cs-available"></div>
        </section>
      </div>
    </div>
  `;
  document.body.append(root);

  const $ = <T extends HTMLElement>(id: string): T => root.querySelector(`#${id}`) as T;
  const canvas = $<HTMLCanvasElement>('cs-canvas');
  const ctx = canvas.getContext('2d');

  let cityId: number | null = null;

  const engine = (): Engine => options.getEngine();
  const city = (): City | null => (cityId === null ? null : (engine().state.cities[cityId] ?? null));

  /**
   * Say what happened, here rather than only in the map log.
   *
   * The log lives behind this screen, so a refusal reported only there is a refusal
   * nobody reads — which is how "clicking that tile does nothing" gets reported as a
   * bug when the engine was explaining itself all along.
   */
  function status(message: string, kind: 'ok' | 'err' | '' = ''): void {
    const el = $('cs-status');
    el.textContent = message;
    el.className = `cs-status ${kind}`;
  }

  function run(command: Parameters<Engine['apply']>[0], success?: string): void {
    const result = engine().apply(command);
    if (!result.ok) {
      status(ERROR_TEXT[result.error], 'err');
      options.onChanged();
      return;
    }
    status(success ?? '', 'ok');
    options.onChanged(success, 'ok');
    refresh();
  }

  // ── the tile picker ─────────────────────────────────────────────────────────

  /** Screen position of a tile in the picker, relative to the city at the centre. */
  function pickerPosition(centre: Hex, h: Hex): { x: number; y: number } {
    const delta = { q: h.q - centre.q, r: h.r - centre.r };
    const point = hexToPixel(delta, PICKER_TILE);
    return { x: canvas.width / 2 + point.x, y: canvas.height / 2 + point.y };
  }

  function drawPicker(): void {
    const target = city();
    if (!ctx || !target) return;
    const { state, content } = engine();
    const map = state.map;
    const centreIndex = cityIndex(map, target);
    const centre = indexToHex(map, centreIndex);
    const corners = hexCorners(PICKER_TILE);

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (const h of [centre, ...workableTiles(map, content, target).map((i) => indexToHex(map, i))]) {
      const index = hexIndex(map, h);
      if (index === -1) continue;
      const { x, y } = pickerPosition(centre, h);

      const terrain = content.terrain.get(map.terrain[index]!);
      const feature = content.features.get(map.feature[index]!);
      const colour = feature.key !== 'none' && feature.colour.length >= 7 ? feature.colour : terrain.colour;

      ctx.beginPath();
      ctx.moveTo(x + corners[0]!.x, y + corners[0]!.y);
      for (let i = 1; i < 6; i++) ctx.lineTo(x + corners[i]!.x, y + corners[i]!.y);
      ctx.closePath();
      ctx.fillStyle = colour;
      ctx.fill();

      const isCentre = index === centreIndex;
      const worked = target.workedTiles.includes(index);
      const available = isCentre || worked || canWorkTile(state, content, target, index);

      if (!available) {
        // Ground this city cannot use: dimmed rather than hidden, so the player can
        // see that it exists and that somebody else has it.
        ctx.fillStyle = 'rgba(8,14,22,0.66)';
        ctx.fill();
      }

      ctx.strokeStyle = isCentre ? '#F2F6F4' : worked ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.35)';
      ctx.lineWidth = isCentre || worked ? 2.5 : 1;
      ctx.stroke();

      // As the city's government lets the tile be worked (ADR-0059).
      const shown = isCentre ? cityCentreYield(engine().state, content, target) : workedYield(engine().state, content, target.owner, index);
      if (isCentre || worked) {
        ctx.font = '600 10px ui-monospace, monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'rgba(8,14,22,0.85)';
        ctx.fillText(`${shown.food}/${shown.production}/${shown.gold}`, x, y + PICKER_TILE * 0.42);
      }

      if (isCentre) {
        ctx.beginPath();
        ctx.arc(x, y - 4, PICKER_TILE * 0.26, 0, Math.PI * 2);
        ctx.fillStyle = state.civs[target.owner]?.colour ?? '#FFFFFF';
        ctx.fill();
        ctx.strokeStyle = 'rgba(8,14,22,0.9)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    }
  }

  canvas.addEventListener('click', (event) => {
    const target = city();
    if (!target) return;
    const { state } = engine();
    const centre = indexToHex(state.map, cityIndex(state.map, target));

    const rect = canvas.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * canvas.width - canvas.width / 2;
    const y = ((event.clientY - rect.top) / rect.height) * canvas.height - canvas.height / 2;

    const delta = pixelToHex(x, y, PICKER_TILE);
    const hex: Hex = { q: centre.q + delta.q, r: centre.r + delta.r };
    const index = hexIndex(state.map, hex);
    if (index === -1 || index === hexIndex(state.map, centre)) return;

    run({ t: 'ToggleWorkedTile', cityId: target.id, tile: index });
  });

  // ── figures ─────────────────────────────────────────────────────────────────

  function drawFigures(): void {
    const target = city();
    const list = $('cs-yields');
    if (!target) {
      list.innerHTML = '';
      return;
    }
    const { state, content } = engine();
    const yields = cityYield(state, content, target);
    const eaten = target.population * content.rules.cities.foodPerCitizen;
    const surplus = foodSurplus(state, content, target);
    const threshold = growthThreshold(content, target.population);

    let growth: string;
    if (isBesieged(state, content, target)) growth = 'besieged — no food comes in, defenders cannot heal';
    else if (isAtSizeCap(content, target)) growth = capText(content, target);
    else if (surplus > 0) growth = `${Math.ceil((threshold - target.foodStore) / surplus)} turns`;
    else if (surplus < 0) growth = 'starving';
    else growth = 'not growing';

    const head = target.queue[0];
    const build = head
      ? heldForPopulation(content, target, head)
        ? `${itemName(content, head)} — waiting for citizens`
        : yields.production > 0
          ? `${itemName(content, head)} in ${Math.max(1, Math.ceil((itemCost(content, head, engine().state.civs[target.owner]) - target.productionStore) / yields.production))} turns`
          : `${itemName(content, head)} — no production`
      : 'nothing';

    const net = effectiveYield(state, content, target);
    const drag = dragFraction(state, content, target);
    const feeling = mood(state, content, target);
    const accounts = budget(state, content, target.owner);

    rows(list, [
      [
        'food',
        foodIsWasted(content, target) && surplus > 0
          ? `${yields.food} grown · ${eaten} eaten · +${surplus} thrown away, the store is full`
          : `${yields.food} grown · ${eaten} eaten · ${surplus >= 0 ? '+' : ''}${surplus}`,
      ],
      ['store', `${target.foodStore} of ${threshold}`],
      ['growth', growth],
      ['production', feeling.disorder ? `${yields.production} produced, none of it kept` : `${net.production} per turn · ${target.productionStore} stored`],
      ['building', feeling.disorder ? 'nothing — the city is in disorder' : build],
      ['gold', drag > 0 ? `${net.gold} reaches the treasury, of ${yields.gold} raised` : `${net.gold} per turn`],
      ['distance', drag > 0 ? `${Math.round(drag * 100)}% lost between here and the capital` : 'this is the capital'],
      [
        'mood',
        feeling.disorder
          ? `IN DISORDER — ${feeling.discontented} discontented against ${feeling.content} content`
          : `${feeling.content} content · ${feeling.discontented} discontented`,
      ],
      [
        'settled by',
        `${feeling.fromBuildings} from buildings and wonders · ${feeling.fromGarrison} from the garrison` +
          (feeling.fromGovernment > 0 ? ` · ${feeling.fromGovernment} by the government` : '') +
          (feeling.fromSpecialists > 0 ? ` · ${feeling.fromSpecialists} by entertainers` : '') +
          (feeling.fromWeariness > 0 ? ` · ${feeling.fromWeariness} lost to war weariness` : ''),
      ],
      ['culture', cultureLine(state, content, target)],
      ['resources', resourceLine(state, content, target)],
      ['working', `${target.workedTiles.length} on tiles${spareCitizens(target) > 0 ? ` · ${describeSpecialists(target)}` : ''}`],
      ['governor', target.governed ? 'arranging tiles' : 'set by hand'],
      ['empire', `${state.civs[target.owner]?.treasury ?? 0}g · ${accounts.net >= 0 ? '+' : ''}${accounts.net} per turn`],
      ['founded', `turn ${target.foundedTurn}`],
    ]);

    const built = $('cs-built');
    built.innerHTML = target.buildings.length
      ? target.buildings
          .map((id) => {
            const def = content.buildings.get(id);
            return `<span class="cs-chip${def.wonder ? ' wonder' : ''}" title="${escapeHtml(def.description)}">${def.wonder ? '★ ' : ''}${escapeHtml(def.name)}</span>`;
          })
          .join('')
      : '<span class="dim">Nothing yet.</span>';
  }

  /** "2 scholars · 1 entertainer", in content order, naming only what there is. */
  function describeSpecialists(target: City): string {
    const { state, content } = engine();
    const counts = specialistsOf(state, content, target);
    return content.specialists.all
      .filter((s) => counts[s.id]! > 0)
      .map((s) => `${counts[s.id]} ${(counts[s.id] === 1 ? s.name : s.plural).toLowerCase()}`)
      .join(' · ');
  }

  function drawSpecialists(): void {
    const target = city();
    if (!target) return;
    const { content } = engine();
    const select = $<HTMLSelectElement>('cs-spec');
    select.innerHTML =
      `<option value="-1">Auto — entertainers if needed, else ${escapeHtml(content.specialists.byKey.get(content.rules.cities.autoSpecialist)!.plural.toLowerCase())}</option>` +
      content.specialists.all.map((s) => `<option value="${s.id}">${escapeHtml(s.plural)}</option>`).join('');
    select.value = String(target.specialist);
    const spare = spareCitizens(target);
    const chosen = target.specialist >= 0 ? content.specialists.get(target.specialist) : null;
    $('cs-spec-note').textContent =
      (chosen ? chosen.description : 'On Auto the governor will take a citizen off a tile to entertain rather than let the city riot.') +
      (spare > 0 ? ` Now: ${describeSpecialists(target)}.` : ' Every citizen is working a tile.');
  }

  // ── production ──────────────────────────────────────────────────────────────

  function drawProduction(): void {
    const target = city();
    const queueEl = $('cs-queue');
    const availableEl = $('cs-available');
    if (!target) {
      queueEl.innerHTML = '';
      availableEl.innerHTML = '';
      return;
    }
    const { content } = engine();
    const civ = engine().state.civs[target.owner];
    if (!civ) return;

    queueEl.innerHTML = target.queue.length
      ? target.queue
          .map(
            (item, position) =>
              `<div class="cs-row"><span>${position + 1}. ${escapeHtml(itemName(content, item))}</span>
               <span class="dim">${itemCost(content, item, engine().state.civs[target.owner])}</span>
               <button data-up="${position}" ${position === 0 ? 'disabled' : ''} title="Build this sooner">↑</button>
               <button data-down="${position}" ${position === target.queue.length - 1 ? 'disabled' : ''} title="Build this later">↓</button>
               <button data-remove="${position}" title="Remove">×</button></div>`,
          )
          .join('')
      : '<div class="dim">This city is building nothing.</div>';

    for (const button of queueEl.querySelectorAll<HTMLButtonElement>('button[data-remove]')) {
      button.addEventListener('click', () => {
        const position = Number(button.dataset['remove']);
        const lose = position === 0 ? switchCost(content, target, target.queue[1] ?? null) : 0;
        run(
          { t: 'DequeueItem', cityId: target.id, position },
          lose > 0 ? `Changed plans — ${lose} production lost.` : undefined,
        );
      });
    }

    // Reordering behind the head is free; promoting something past it is a switch, so
    // the cost is shown on the button before it is paid.
    for (const [attribute, delta] of [
      ['up', -1],
      ['down', 1],
    ] as const) {
      for (const button of queueEl.querySelectorAll<HTMLButtonElement>(`button[data-${attribute}]`)) {
        const from = Number(button.dataset[attribute]);
        const to = from + delta;
        const willSwitch = from === 0 || to === 0;
        const lose = willSwitch ? switchCost(content, target, target.queue[to] ?? null) : 0;
        if (lose > 0) button.title = `Build this sooner — costs ${lose} of ${target.productionStore} stored`;
        button.addEventListener('click', () => {
          run(
            { t: 'MoveQueueItem', cityId: target.id, from, to },
            lose > 0 ? `Changed plans — ${lose} production lost.` : undefined,
          );
        });
      }
    }

    const items = buildableItems(content, target, civ, engine().state.map).filter(
      // A wonder someone has already finished is gone from the list for good.
      (item) => wonderBlocker(engine().state, content, target, item) !== 'WonderBuilt',
    );
    const access = resourceAccess(engine().state, content, target.owner);
    availableEl.innerHTML = items
      .map((item, i) => {
        const blocker = buildBlocker(target, item);
        // A unit needing a strategic resource this city lacks: listed, so the player
        // knows it exists and why it cannot be built (ADR-0063).
        const missing = resourceBlocker(engine().state, content, target, item, access);
        if (missing !== -1) {
          return `<div class="cs-row soon"><span>${escapeHtml(itemName(content, item))}</span>
            <span class="dim" title="Join a deposit to this city by road or river, or trade for it">needs ${escapeHtml(content.resources.get(missing).name)}</span></div>`;
        }
        const def = item.kind === 'building' ? content.buildings.get(item.building) : null;
        const elsewhere =
          def?.wonder && wonderBlocker(engine().state, content, target, item) === 'WonderUnderway'
            ? wonderUnderwayIn(engine().state, target.owner, def.id)
            : null;
        const note =
          (def?.wonder ? ' <span class="wonder-tag" title="A world wonder: only one can ever be built">★ wonder</span>' : '') +
          (blocker === 'AlreadyQueued' ? ' <span class="dim">queued</span>' : '') +
          (elsewhere ? ` <span class="dim">being built in ${escapeHtml(elsewhere.name)}</span>` : '');
        // Switching between a unit and a building loses part of the store. Shown here,
        // on the button, rather than discovered afterwards — a penalty the player finds
        // out about later is a trap; one they can see is a decision.
        const lose = switchCost(content, target, item);
        const warning = lose > 0 ? ` <span class="warn-inline">−${lose}</span>` : '';
        const title = lose > 0 ? `Build this next — loses ${lose} of ${target.productionStore} stored` : 'Build this next';
        return `<div class="cs-row"><span>${escapeHtml(itemName(content, item))}${note}</span>
          <span class="dim">${itemCost(content, item, engine().state.civs[target.owner])}${warning}</span>
          <button data-build="${i}" ${elsewhere ? 'disabled' : ''} title="${elsewhere ? 'One city at a time may build a wonder' : title}">Build</button>
          <button data-queue="${i}" ${blocker || elsewhere ? 'disabled' : ''} title="Add to the queue — costs nothing">+</button></div>`;
      })
      .join('') + soonRows();

    // What the next discovery would add to this list, greyed, so the player can see what
    // research buys without opening the tree. Only technologies ready to research now —
    // listing the whole future would bury the list.
    function soonRows(): string {
      const soon = comingSoon(content, target!, civ!, engine().state.map);
      if (soon.length === 0) return '';
      return (
        `<div class="cs-soon-head">With research</div>` +
        soon
          .map(
            (item) => `<div class="cs-row soon"><span>${escapeHtml(itemName(content, item))}</span>
              <span class="dim">needs ${escapeHtml(content.technologies.get(itemTech(content, item)).name)}</span></div>`,
          )
          .join('')
      );
    }

    const bind = (attribute: 'build' | 'queue', make: (item: ProductionItem) => Parameters<Engine['apply']>[0]): void => {
      for (const button of availableEl.querySelectorAll<HTMLButtonElement>(`button[data-${attribute}]`)) {
        button.addEventListener('click', () => {
          const item = items[Number(button.dataset[attribute])];
          if (item) run(make(item));
        });
      }
    };
    bind('build', (item) => ({ t: 'SetProduction', cityId: target.id, item }));
    bind('queue', (item) => ({ t: 'QueueItem', cityId: target.id, item }));
  }

  // ── plumbing ────────────────────────────────────────────────────────────────

  function refresh(): void {
    const target = city();
    if (!target) {
      close();
      return;
    }
    const { content } = engine();
    $('cs-name').textContent = target.name;
    $('cs-sub').textContent = `Size ${target.population} of ${sizeCap(content, target)} · founded turn ${target.foundedTurn}`;
    const spare = foodSurplus(engine().state, content, target);
    const hint = $('cs-tilehint');
    // The governor works shields once spare food has nowhere to go (ADR-0069); a city
    // arranged by hand is told instead, because the choice is the player's.
    const wasting = !target.governed && foodIsWasted(content, target) && spare > 0;
    hint.classList.toggle('wasting', wasting);
    hint.textContent = target.governed
      ? 'The governor is arranging these. Click a tile to take over.'
      : wasting
        ? `${spare} food a turn is going to waste: the city is at its size limit and its store is full. Work shields or trade instead, or return the tiles to the governor.`
        : 'Arranged by hand. Click a worked tile to free the citizen.';
    drawPicker();
    drawFigures();
    drawSpecialists();
    drawProduction();
  }

  function open(id: number): void {
    cityId = id;
    root.hidden = false;
    status('');
    refresh();
  }

  function close(): void {
    cityId = null;
    root.hidden = true;
    options.onChanged();
  }

  $('cs-close').addEventListener('click', close);
  $<HTMLSelectElement>('cs-spec').addEventListener('change', (event) => {
    const target = city();
    const value = Number((event.target as HTMLSelectElement).value);
    if (target) run({ t: 'SetSpecialist', cityId: target.id, specialist: value });
  });
  $('cs-auto').addEventListener('click', () => {
    const target = city();
    if (target) run({ t: 'AutoArrange', cityId: target.id }, 'The governor has the tiles again.');
  });
  root.addEventListener('click', (event) => {
    if (event.target === root) close(); // clicking the backdrop
  });

  return {
    open,
    close,
    get isOpen(): boolean {
      return !root.hidden;
    },
    refresh,
  };
}

function rows(target: HTMLElement, entries: Array<[string, string]>): void {
  target.innerHTML = entries.map(([k, v]) => `<dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd>`).join('');
}

const escapeHtml = (text: string): string => text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

/** Kept so the module's content import is used by the type checker. */
export type { Content };
